import React from 'react';
import { SPACING_12, SPACING_8 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { IconProps } from 'components/Icon';
import _merge from 'lodash/merge';

import { getContext } from 'common';

import Styled from './styles';

export interface SearchwChipsCoreProps extends React.ComponentPropsWithRef<'div'> {
  /** Set `true`, to render in focused visual. */
  focused?: boolean;
  /** Set `true`, to render in hovered visual. */
  hovered?: boolean;
  /** The size of the Chips. */
  size?: 'small' | 'medium';
  /** The FilterField styling variant. */
  variant?: 'outlined' | 'underlined';
  /** The content of the chip, including `<Chip />`, `<TextInput />` */
  children: React.ReactNode;
  /** The icon of component. */
  SIcon?: (arg: Partial<IconProps>) => JSX.Element;
  /** Any element such as <SearchwChipsGlobalLogic /> that is rendered before chips (on the left). */
  beforeChips?: React.ReactNode;
  /** Any element such as <IconButton /> that is rendered after chips (on the right). */
  afterChips?: React.ReactNode;
  /** Properties to apply to the children wrapper element. */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** To override the vertical divider. */
  verticalDivider?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Any other props than the `SearchwChipsCore` props will be provided to the root element. This component can be used to make different variant of the SearchwChips. */
const SearchwChipsCore = React.forwardRef<HTMLElement, SearchwChipsCoreProps>((props: SearchwChipsCoreProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    children,
    SIcon,
    size,
    variant,
    wrapperProps,
    beforeChips,
    afterChips,
    verticalDivider,
    theme: themeProp,
    ...otherProps
  } = props;
  const { container: containerTheme } = themeProp;
  const resolvedTheme = _merge({}, theme.SEARCH_W_CHIPS.container, containerTheme);
  const LTRStyle = size === 'small' ?
    { margin: variant === 'outlined' ? `${SPACING_8} 0 0 ${SPACING_8}` : `${SPACING_8} 0 0 0`, }
    :
    { margin: variant === 'outlined' ? `${SPACING_12} 0 0 ${SPACING_8}` : `${SPACING_12} 0 0 0`, };
  const RTLStyle = size === 'small' ?
    { margin: variant === 'outlined' ? `${SPACING_8} ${SPACING_8} 0 0` : `${SPACING_8} 0 0 0`, }
    :
    { margin: variant === 'outlined' ? `${SPACING_12} ${SPACING_8} 0 0` : `${SPACING_12} 0 0 0`, };
  return (
    <Styled.SearchwChipsCore
      ref={ref}
      RTL={RTL}
      size={size}
      variant={variant}
      $theme={resolvedTheme}
      $disableAnimation={disableAnimation}
      {...otherProps}
    >
      {SIcon({
        size: size === 'small' ? '1.25rem' : '1.5rem',
        style: RTL ? RTLStyle : LTRStyle,
      })}
      {beforeChips && (
        <Styled.ExtraElementsWrapper RTL={RTL}>
          {beforeChips}
        </Styled.ExtraElementsWrapper>)}
      {verticalDivider}
      <Styled.ChipsWrapper role="search" {...wrapperProps}>
        {children}
      </Styled.ChipsWrapper>
      {afterChips && <Styled.ExtraElementsWrapper RTL={RTL}>{afterChips}</Styled.ExtraElementsWrapper>}
    </Styled.SearchwChipsCore>
  );
}));

SearchwChipsCore.displayName = 'SearchwChipsCore';

SearchwChipsCore.defaultProps = {
  focused: false,
  SIcon: () => undefined,
  beforeChips: undefined,
  afterChips: undefined,
  verticalDivider: false,
  size: 'small',
  variant: 'underlined',
  wrapperProps: {},
  theme: {},
  hovered: false
};

export default SearchwChipsCore;
